import { collection, addDoc, query, where, getDocs, doc, deleteDoc, setDoc } from "firebase/firestore";
import { db, auth } from "../firebase";

// Convierte string a número seguro (acepta coma o punto)
export function stringToNumber(str) {
  if (!str) return 0;
  const num = parseFloat(str.toString().replace(',', '.'));
  return isNaN(num) ? 0 : num;
}

// Formatea fecha ISO a formato legible
export function formatearFecha(fechaISO) {
  const fecha = new Date(fechaISO);
  return `${fecha.toLocaleDateString()} ${fecha.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`;
}

// Convierte fecha ISO a formato para input datetime-local
export function isoToLocalDatetime(isoString) {
  const date = new Date(isoString);
  return date.toISOString().slice(0, 16);
}

// Convierte datetime-local a formato ISO
export function localDatetimeToIso(localDatetime) {
  return new Date(localDatetime).toISOString();
}

// Formatea input numérico y evita 0 inicial
export function formatearNumero(valor) {
  if (valor === '0') return '';
  return valor.toString().replace(/^0+(?=\d)/, '');
}

// Local storage helpers for custom foods
export const LOCAL_FOODS_KEY = 'customFoods';
export const LOCAL_DELETED_FOODS_KEY = 'deletedCustomFoods';

export function getLocalFoods() {
  try {
    const data = localStorage.getItem(LOCAL_FOODS_KEY);
    let foods = data ? JSON.parse(data) : [];
    // Filtrar los eliminados
    const deleted = getDeletedCustomFoods();
    foods = foods.filter(f => !deleted.includes(f.nombre.toLowerCase()));
    return foods;
  } catch {
    return [];
  }
}

export function saveLocalFood(food) {
  let foods = getLocalFoods();
  // Eliminar si ya existe (por nombre)
  foods = foods.filter(f => f.nombre.toLowerCase() !== food.nombre.toLowerCase());
  foods.push(food);
  localStorage.setItem(LOCAL_FOODS_KEY, JSON.stringify(foods));
}

export function clearLocalFoods() {
  localStorage.removeItem(LOCAL_FOODS_KEY);
}

export function getDeletedCustomFoods() {
  try {
    const data = localStorage.getItem(LOCAL_DELETED_FOODS_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

export function addDeletedCustomFood(nombre) {
  const deleted = getDeletedCustomFoods();
  if (!deleted.includes(nombre.toLowerCase())) {
    deleted.push(nombre.toLowerCase());
    localStorage.setItem(LOCAL_DELETED_FOODS_KEY, JSON.stringify(deleted));
  }
}

export function clearDeletedCustomFoods() {
  localStorage.removeItem(LOCAL_DELETED_FOODS_KEY);
}

// Local storage helpers for diario
export const LOCAL_DIARIO_KEY = 'customDiario';

export function getLocalDiario() {
  try {
    const data = localStorage.getItem(LOCAL_DIARIO_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

export function saveLocalDiarioEntry(entry) {
  const diario = getLocalDiario();
  diario.unshift(entry); // keep most recent first
  localStorage.setItem(LOCAL_DIARIO_KEY, JSON.stringify(diario));
}

export function clearLocalDiario() {
  localStorage.removeItem(LOCAL_DIARIO_KEY);
}

// Local storage helpers for common foods
export const LOCAL_COMMON_FOODS_KEY = 'commonFoodsBackup';
export const LOCAL_COMMON_FOODS_PENDING_KEY = 'commonFoodsPending';

export function saveCommonFoodsToLocal(foods) {
  localStorage.setItem(LOCAL_COMMON_FOODS_KEY, JSON.stringify(foods));
}

export function getCommonFoodsFromLocal() {
  try {
    const data = localStorage.getItem(LOCAL_COMMON_FOODS_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

export function savePendingCommonFood(food) {
  const pending = getPendingCommonFoods();
  pending.push(food);
  localStorage.setItem(LOCAL_COMMON_FOODS_PENDING_KEY, JSON.stringify(pending));
}

export function getPendingCommonFoods() {
  try {
    const data = localStorage.getItem(LOCAL_COMMON_FOODS_PENDING_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

export function clearPendingCommonFoods() {
  localStorage.removeItem(LOCAL_COMMON_FOODS_PENDING_KEY);
}

// Helpers para alimentos ocultos comunes
export const LOCAL_HIDDEN_COMMON_FOODS_KEY = 'hiddenCommonFoods';

export function getHiddenCommonFoods() {
  try {
    const data = localStorage.getItem(LOCAL_HIDDEN_COMMON_FOODS_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

export function addHiddenCommonFood(nombre) {
  const hidden = getHiddenCommonFoods();
  if (!hidden.includes(nombre.toLowerCase())) {
    hidden.push(nombre.toLowerCase());
    localStorage.setItem(LOCAL_HIDDEN_COMMON_FOODS_KEY, JSON.stringify(hidden));
  }
}

export function removeHiddenCommonFood(nombre) {
  let hidden = getHiddenCommonFoods();
  hidden = hidden.filter(n => n !== nombre.toLowerCase());
  localStorage.setItem(LOCAL_HIDDEN_COMMON_FOODS_KEY, JSON.stringify(hidden));
}

export function clearHiddenCommonFoods() {
  localStorage.removeItem(LOCAL_HIDDEN_COMMON_FOODS_KEY);
}

// Save or update a food to Firestore using setDoc (unique by userId+nombre)
export const saveFood = async (food) => {
  if (!auth.currentUser) {
    console.error('No user logged in! Food not saved to Firebase.');
    return null;
  }
  try {
    const { id, ...foodData } = food;
    const foodId = `${auth.currentUser.uid}_${foodData.nombre.toLowerCase()}`;
    await setDoc(doc(db, "foods", foodId), {
      ...foodData,
      userId: auth.currentUser.uid,
      userName: auth.currentUser.displayName,
      userEmail: auth.currentUser.email,
    });
    return foodId;
  } catch (error) {
    console.error("Error saving food:", error);
    return null;
  }
};

// Added deleteFood to remove a food from Firestore
export const deleteFood = async (foodId) => {
  try {
    const foodRef = doc(db, "foods", foodId);
    await deleteDoc(foodRef);
    console.log("Food deleted with ID:", foodId);
  } catch (error) {
    console.error("Error deleting food:", error);
  }
};

// Save or update a diario entry to Firestore using setDoc (unique by userId+fecha+alimentos)
export const saveDiarioEntry = async (entry) => {
  if (!auth.currentUser) {
    console.error('No user logged in! Diario entry not saved to Firebase.');
    return null;
  }
  try {
    const { id, ...entryData } = entry;
    // Use fecha + alimentos hash as unique id
    const alimentosKey = (entryData.alimentos||[]).map(a=>a.nombre).join(',').toLowerCase();
    const entryId = `${auth.currentUser.uid}_${entryData.fecha}_${alimentosKey}`;
    await setDoc(doc(db, "diario", entryId), {
      ...entryData,
      userId: auth.currentUser.uid,
      userName: auth.currentUser.displayName,
      userEmail: auth.currentUser.email,
    });
    return entryId;
  } catch (error) {
    console.error("Error saving diario entry:", error);
    return null;
  }
};

// Fetch foods for the logged-in user
export const fetchFoods = async () => {
  try {
    const q = query(collection(db, "foods"), where("userId", "==", auth.currentUser.uid));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching foods:", error);
    return [];
  }
};

// Fetch diario entries for the logged-in user
export const fetchDiarioEntries = async () => {
  try {
    const q = query(collection(db, "diario"), where("userId", "==", auth.currentUser.uid));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching diario entries:", error);
    return [];
  }
};

// Fetch common foods from Firestore
export const fetchCommonFoods = async () => {
  try {
    const q = collection(db, "commonFoods");
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching common foods:", error);
    return [];
  }
};

// Utility functions for cookies
export const setCookie = (name, value, days) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=None; Secure`;
};

export const getCookie = (name) => {
  return document.cookie.split('; ').find(row => row.startsWith(name + '='))?.split('=')[1];
};
